import EnvConfig from "../environments/envConfig";
import { getGroupId, getTokenFromLocal } from "../helpers/appHelpers";
import { authTokenDedicated } from "../helpers/authenticationHelpers";
import { front_api } from "./apiConfig";
import { 
    generateVaultKeypair, 
    deriveWrappingKey, 
    wrapPrivateKey,
    encryptPasswordWithPublicKey
} from "../helpers/cryptoHelper";


export const login = async (username: string, password: string) => {
    try {
        const response = await front_api("POST", `/login`, { username, password }, { wrapData: false })
        if (!response) {
            return { success: false, message: "ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้" };
        }
        const data = await response.json();

        if (data.success) {
            const authResult = authTokenDedicated(data.access_token, data.refresh_token);

            // Auto-initialize vault during login if configured but empty (only for Admin role)
            try {
                const payloadBase64 = data.access_token.split('.')[1];
                const decodedPayload = JSON.parse(window.atob(payloadBase64));
                const roleName = decodedPayload.role_name;

                if (roleName === "Admin") {
                    const config = await getSecurePasswordConfig();
                    if (config && config.enabled && !config.initialized) {
                        // 1. Generate P-256 Keypair
                        const { publicKeyPem, privateKeyBase64 } = await generateVaultKeypair();

                        // 2. Generate random crypto_salt
                        const randomBytes = window.crypto.getRandomValues(new Uint8Array(16));
                        let saltHex = '';
                        for (let i = 0; i < randomBytes.length; i++) {
                            saltHex += ('0' + randomBytes[i].toString(16)).slice(-2);
                        }

                        // 3. Derive wrapping key from password
                        const wrappingKey = await deriveWrappingKey(password, saltHex);

                        // 4. Wrap private key
                        const { ciphertextBase64, nonceBase64 } = await wrapPrivateKey(privateKeyBase64, wrappingKey);

                        // 5. Encrypt password copy using public key
                        const esc = await encryptPasswordWithPublicKey(password, publicKeyPem);
                        const escPasswordEncrypted = JSON.stringify({
                            epk: esc.ephemeralPkPem,
                            cipher: esc.ciphertextBase64,
                            nonce: esc.nonceBase64
                        });

                        // 6. Initialize secure vault key on backend
                        await initSecurePassword({
                            vault_public_key: publicKeyPem,
                            vault_private_key_wrapped: ciphertextBase64,
                            vault_private_key_nonce: nonceBase64,
                            crypto_salt: saltHex,
                            esc_password_encrypted: escPasswordEncrypted
                        });
                    }
                }
            } catch (e) {
                console.error("Auto init vault during login failed:", e);
            }

            return {  authResult,
                success: true };
        } else {
            return { 
                success: false, 
                message: data.error || "Username หรือ Password ไม่ถูกต้อง" 
            };
        }
    } catch (error) {
        console.error("Login Error:", error);
        return { success: false, message: "เกิดข้อผิดพลาดในการเข้าสู่ระบบ" };
    }
};

export const logout = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    
    // เรียก API ลบ token ใน database
    await front_api("POST", `/logout`, { refresh_token: refreshToken }, { wrapData: false });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // ลบ tokens ใน browser ไม่ว่า API จะสำเร็จหรือไม่
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    // redirect ไปหน้า login
    window.location.href = '/login';
  }
};




export const refresh = async (refresh_token: string) => {
    try {
        const env = new EnvConfig()
        const response = await fetch(`${env.front_api}/refresh-token`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ refresh_token }),
        });
        return response;
    } catch (error) {
        console.error("Refresh Error:", error);
        return false;
    }
};

export const register = async (username: string , password: string) => {
    const body = {
        username:username,
        password:password
    }
    try {
        const response = await front_api("POST", `/register`, body, { wrapData: false })
        if (!response) return false

        const result = await response.json()

        if (!response.ok) {
            return { ...result, success: false }
        }

        return result
    } catch (error) {
        return false
    }
}

export const createUser = async (username: string , password: string , role_id: number, extraCryptoParams: any = null) => {
    const body = {
        username:username,
        password:password,
        role_id:role_id,
        ...extraCryptoParams
    }
    try {
        const response = await front_api("POST", `/create_user`, body, { wrapData: false })
        if (!response) return false

        const result = await response.json()

        if (!response.ok) {
            return { ...result, success: false }
        }

        return result
    } catch (error) {
        return false
    }
}
export const getUserList = async (
    page: number,
    limit: number,
    search: string = "",
    roleId?: number | string,
) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            pageConfig: limit.toString(),
        });
        
        if (search) params.append("search", search); 
        
        if (roleId && roleId !== "") params.append("filter", roleId.toString()); 

        const response = await front_api("GET", `/get_user_list?${params.toString()}`, {}, { wrapData: false });
        
        if (!response) return false;
        return await response.json();
    } catch (error) {
        return false;
    }
}

// Edit User role
export const editUser = async (data: any) => {
    const body = {
        username: data.username,
        role_id: data.role_id,
        updated_by: "system"
    }
    try {
        const response = await front_api("PUT", `/change_user_role`, body, { wrapData: false })
        if (!response) return false

        const result = await response.json()

        if (!response.ok) {
            return { ...result, success: false }
        }

        return result
    } catch (error) {
        return false
    }
}

// Change Password (self-service — requires old password)
export const changePassword = async (data: any) => {
    const body = {
        username: data.username,
        old_password: data.old_password,
        new_password: data.new_password
    }
    try {
        const response = await front_api("PUT", `/change_user_password`, body, { wrapData: false })
        if (!response) return false

        const result = await response.json()

        if (!response.ok) {
            return { ...result, success: false }
        }

        return result
    } catch (error) {
        return false
    }
}

// Admin Reset Password (admin-only — no old password required)
export const adminResetPassword = async (username: string, newPassword: string, extraCryptoParams: any = null) => {
    const body = {
        username,
        new_password: newPassword,
        ...extraCryptoParams
    };
    try {
        const response = await front_api("PUT", `/change_user_password`, body, { wrapData: false });
        if (!response) return { success: false, message: "ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้" };

        const result = await response.json();

        if (!response.ok) {
            return { ...result, success: false, message: result.message || result.details || "เกิดข้อผิดพลาดจากเซิร์ฟเวอร์" };
        }

        return { ...result, success: true };
    } catch (error: any) {
        console.error("adminResetPassword Error:", error);
        return { success: false, message: error.message || "เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ" };
    }
};


export const deleteUser = async (username: string) => {
    try {
        const response = await front_api("DELETE", `/delete_user`, { username }, { wrapData: false });
        if (!response) {
            return { success: false, message: "ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้" };
        }
        const result = await response.json();
        if (!response.ok) {
            return { ...result, success: false, message: result.message || result.error || "เกิดข้อผิดพลาดจากทางเซิร์ฟเวอร์" };
        }
        return { ...result, success: true };
    } catch (error: any) {
        console.error("API deleteUser Error:", error);
        return { success: false, message: error.message || "เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ" };
    }
};

export const banUser = async (data: any) => {
    const body = {
        username: data.username,
        is_active: data.is_active,
        updated_by: data.updated_by
    };

    try {
        const response = await front_api("PUT", `/ban_user`, body, { wrapData: false });
        if (!response) {
            return { success: false, message: "ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้" };
        }

        const result = await response.json();
        if (!response.ok) {
            return { 
                ...result, 
                success: false, 
                message: result.message || "เกิดข้อผิดพลาดจากทางเซิร์ฟเวอร์" 
            };
        }
        return { ...result, success: true };

    } catch (error: any) {
        console.error("API banUser Error:", error);
        return { 
            success: false, 
            message: error.message || "เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ" 
        };
    }
};

export const authPlatform = async (platform_id : number) => {
    const response = await front_api("GET", `/auth_platform/${platform_id}`, {}, { wrapData: false });

    if (!response) return false;

    try {
        if (!response.ok) {
            const errData = await response.json();
            return { success: false, message: errData.details || errData.error || `เกิดข้อผิดพลาด (${response.status})` };
        }
        return await response.json();
    } catch (e) {
        console.error(e);
        return false;
    }
}

export const getSecurePasswordConfig = async () => {
    try {
        const response = await front_api("GET", `/secure-passwords/config`, {}, { wrapData: false });
        if (!response) return { success: false, message: "ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้" };
        const result = await response.json();
        if (!response.ok) {
            return { ...result, success: false, message: result.message || "เกิดข้อผิดพลาดจากทางเซิร์ฟเวอร์" };
        }
        return result;
    } catch (error: any) {
        console.error("getSecurePasswordConfig Error:", error);
        return { success: false, message: error.message || "เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ" };
    }
};

export const initSecurePassword = async (data: {
    vault_public_key: string;
    vault_private_key_wrapped: string;
    vault_private_key_nonce: string;
    crypto_salt: string;
    esc_password_encrypted?: string;
}) => {
    try {
        const response = await front_api("POST", `/secure-passwords/init`, data, { wrapData: false });
        if (!response) return { success: false, message: "ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้" };
        const result = await response.json();
        if (!response.ok) {
            return { ...result, success: false, message: result.message || "เกิดข้อผิดพลาดจากทางเซิร์ฟเวอร์" };
        }
        return result;
    } catch (error: any) {
        console.error("initSecurePassword Error:", error);
        return { success: false, message: error.message || "เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ" };
    }
};